import React, { useState, useMemo } from "react";
import {
  Box,
  Grid,
  Stack,
  Button,
  useTheme,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import {
  LineChart,
  RadarChart,
  HorizontalBarChart,
} from "../../../../../shared/components/charts";
import {
  DashboardCard,
  DashboardCardSkeleton,
  CohortGuard,
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
  ComparisonToggle,
  ComparisonColumns,
} from "../../../../../shared/components";
import { TextStat } from "../../../../../shared/components/charts/TextStat";
import {
  useWellnessOverviewStats,
  useRecoveryStrainTrends,
  useSleepTrends,
  useHRVTrends,
  useWorkoutTimeTrends,
  useStrainActivityByDepartment,
  useSleepQualityByDepartment,
  useWorkoutActivityByDepartment,
  useWellbeingDistribution,
} from "../hooks/useWellnessOverview";
import { WellnessOverviewStatsColumn } from "../components/WellnessOverviewStatsColumn";
import {
  buildFilterParams,
  transformTrendsData,
  calculateGranularity,
  roundToDecimal,
} from "../../shared/utils";
import { useClientScope } from "../../../../../shared/contexts";
import {
  usePersistedDateRange,
  usePersistedFilters,
  useMetricsOverallRange,
  useComparisonMode,
  useComparisonGroups,
  useComparisonTrends,
  useComparisonDistribution,
} from "../../../../../shared/hooks";
import { buildComparisonTrendSeries } from "../../../../../shared/utils";
import { AiSummaryPanel, useAiSummary } from "../../../insights";

const WELLNESS_OVERVIEW_DETAILS = {
  averageHrv: `Heart rate variability (HRV) refers to the variation in time between successive heartbeats and is a key indicator of autonomic nervous system regulation and recovery status. Higher HRV generally reflects greater parasympathetic (rest-and-digest) activity and adaptive capacity.

The gauge compares current HRV to an individualised baseline derived from prior measurements. Values below baseline may indicate elevated sympathetic activation, stress, or fatigue, while values within or above baseline suggest adequate recovery and balanced autonomic function.`,
  overallEmployeeWellness: `Provides a summary view of three core physiological metrics: recovery, strain, and sleep. Recovery reflects autonomic readiness and restorative status, strain represents cumulative physiological load, and sleep captures both duration and quality of rest.

Together, these measures offer a structured overview of workload balance and recovery adequacy, highlighting broader wellbeing trends and potential areas of sustained stress or resilience across the workforce.`,
  riskBreakdown: `Displays the number of active employees within each predefined risk band (e.g., low, moderate, high), alongside the overall participation rate. Risk bands are typically derived from aggregated physiological and recovery-related indicators, reflecting relative wellbeing status.

This distribution view helps identify the proportion of the workforce potentially at elevated risk, monitor shifts in risk profile over time, and assess engagement levels to ensure data representativeness and programme impact.`,
  strainAndRecovery: `Displays the daily trend of recovery relative to strain, illustrating the relationship between physiological load and restorative capacity over time. Strain reflects cumulative cardiovascular and metabolic demand from activity and stress, while recovery indicates autonomic balance and readiness.

Tracking these metrics together helps assess whether workload is appropriately balanced with adequate recovery, identify periods of sustained high load, and monitor potential fatigue accumulation across the organisation.`,
  organisationHealthOverview: `Provides a benchmark comparison of company-wide health across key physiological metrics, such as recovery, strain, sleep, HRV, and resting heart rate. This overview enables evaluation against defined internal standards, historical baselines, or relevant peer groups.

By aggregating these indicators, it offers a high-level view of overall workforce wellbeing, highlighting strengths, emerging risks, and areas requiring targeted intervention or support.`,
  workoutTime: `Displays organisation-wide trends in total workout duration over time, reflecting overall engagement in structured physical activity. This metric captures cumulative exercise time and highlights changes in participation levels and activity consistency across the workforce.

Monitoring these trends helps identify periods of increased or reduced exercise engagement, supporting assessment of physical activity habits and their potential impact on recovery, cardiovascular health, and overall wellbeing.`,
  averageSleepDuration: `Compares the average number of hours slept per night across the organisation, providing insight into overall sleep quantity and sufficiency. Sleep duration is a key determinant of cognitive performance, physiological recovery, and long-term health.

By analysing trends over time or differences between teams or groups, this metric helps identify patterns of sleep restriction, improvements in sleep behaviour, or variability linked to workload and lifestyle factors. Consistently low average sleep duration may indicate elevated fatigue risk and reduced recovery capacity across the workforce.`,
  departmentPerformance: `Displays average wellness metrics by department, segmented across strain, recovery, and activity. Strain reflects overall physiological load, recovery indicates readiness and autonomic balance, and activity captures movement volume and intensity.

This view enables comparison of workload, recovery capacity, and engagement patterns between teams, helping identify departments experiencing sustained high load, strong recovery balance, or differing activity behaviours.`,
};

export const WellnessOverview: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId, isCentralAdmin, isAnonymised } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters, setFilters } = usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filters);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const [departmentView, setDepartmentView] = useState(
    "strain_activity_by_department"
  );
  // Shared across all comparison columns so they expand the same metric's
  // demographic breakdown together and stay row-aligned.
  const [expandedBreakdownMetric, setExpandedBreakdownMetric] = useState<
    string | null
  >(null);

  const filterParams = useMemo(
    () => buildFilterParams(filters, dateRange, effectiveClientId),
    [filters, dateRange, effectiveClientId]
  );

  const granularity = useMemo(
    () => calculateGranularity(dateRange),
    [dateRange]
  );

  // Comparison mode: render one column/line per selected filter value.
  const comparison = useComparisonMode(filters);
  const { groups, isComparing, overflowCount } = useComparisonGroups(
    filters,
    dateRange,
    effectiveClientId,
    comparison
  );

  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError,
  } = useWellnessOverviewStats(filterParams);

  const {
    data: recoveryStrainTrendsData,
    isLoading: recoveryStrainTrendsLoading,
    error: recoveryStrainTrendsError,
  } = useRecoveryStrainTrends({
    ...filterParams,
    metrics: ["recovery_score", "strain"],
    granularity,
  });

  const {
    data: sleepTrendsData,
    isLoading: sleepTrendsLoading,
    error: sleepTrendsError,
  } = useSleepTrends({
    ...filterParams,
    metrics: ["hours_of_sleep", "hours_of_sleep_needed"],
    granularity,
  });

  const {
    data: hrvTrendsData,
    isLoading: hrvTrendsLoading,
    error: hrvTrendsError,
  } = useHRVTrends({
    ...filterParams,
    metrics: ["hrv", "average_hrv"],
    granularity,
  });

  const {
    data: workoutTimeTrendsData,
    isLoading: workoutTimeTrendsLoading,
    error: workoutTimeTrendsError,
  } = useWorkoutTimeTrends({
    ...filterParams,
    metrics: ["workout_time"],
    granularity,
  });

  const {
    data: strainActivityByDepartmentData,
    isLoading: strainActivityByDepartmentLoading,
    error: strainActivityByDepartmentError,
  } = useStrainActivityByDepartment(filterParams);

  const {
    data: sleepQualityByDepartmentData,
    isLoading: sleepQualityByDepartmentLoading,
    error: sleepQualityByDepartmentError,
  } = useSleepQualityByDepartment(filterParams);

  const {
    data: workoutActivityByDepartmentData,
    isLoading: workoutActivityByDepartmentLoading,
    error: workoutActivityByDepartmentError,
  } = useWorkoutActivityByDepartment(filterParams);

  const {
    data: wellbeingDistributionData,
    isLoading: wellbeingDistributionLoading,
    error: wellbeingDistributionError,
  } = useWellbeingDistribution(filterParams);

  const overallRangeQuery = useMetricsOverallRange(effectiveClientId);

  const overallMinMax = useMemo(() => {
    const metric_min = overallRangeQuery.data?.data?.metric_min ?? {};
    const metric_max = overallRangeQuery.data?.data?.metric_max ?? {};
    return {
      recoveryMin: typeof metric_min.recovery_score === "number" ? metric_min.recovery_score : undefined,
      recoveryMax: typeof metric_max.recovery_score === "number" ? metric_max.recovery_score : undefined,
      strainMin: typeof metric_min.strain === "number" ? metric_min.strain : undefined,
      strainMax: typeof metric_max.strain === "number" ? metric_max.strain : undefined,
      workoutTimeMin: typeof metric_min.workout_time === "number" ? metric_min.workout_time : undefined,
      workoutTimeMax: typeof metric_max.workout_time === "number" ? metric_max.workout_time : undefined,
      hoursOfSleepMin: typeof metric_min.hours_of_sleep === "number" ? metric_min.hours_of_sleep : undefined,
      hoursOfSleepMax: typeof metric_max.hours_of_sleep === "number" ? metric_max.hours_of_sleep : undefined,
    };
  }, [overallRangeQuery.data]);

  // Active user count for cohort privacy guard.
  const activeUserCount = statsData?.data?.active_users?.value ?? statsData?.data?.total_active_employees?.value;

  const handleApplyFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const strainRecoveryTrends = useMemo(() => {
    if (!recoveryStrainTrendsData) return [];
    return transformTrendsData(recoveryStrainTrendsData, [
      "recovery_score",
      "strain",
    ]);
  }, [recoveryStrainTrendsData]);

  const sleepTrends = useMemo(() => {
    if (!sleepTrendsData) return [];
    return transformTrendsData(sleepTrendsData, [
      "hours_of_sleep",
      "hours_of_sleep_needed",
    ]);
  }, [sleepTrendsData]);

  const hrvTrends = useMemo(() => {
    if (!hrvTrendsData) return [];
    return transformTrendsData(hrvTrendsData, ["hrv", "average_hrv"]);
  }, [hrvTrendsData]);

  const workoutTimeTrends = useMemo(() => {
    if (!workoutTimeTrendsData) return [];
    return transformTrendsData(workoutTimeTrendsData, ["workout_time"]);
  }, [workoutTimeTrendsData]);

  // ── Comparison overlays: one line per group on each trend chart ──────────
  const comparisonStrainRecovery = useComparisonTrends(
    groups,
    ["recovery_score", "strain"],
    granularity
  );
  const comparisonWorkout = useComparisonTrends(groups, ["workout_time"], granularity);
  const comparisonSleep = useComparisonTrends(
    groups,
    ["hours_of_sleep", "hours_of_sleep_needed"],
    granularity
  );
  // Per-group wellbeing distribution → one radar polygon per group.
  const comparisonWellbeing = useComparisonDistribution(groups, "wellbeing_dimensions");

  // On-demand AI summary of the metrics on screen.
  const { buildPayload: buildAiSummaryPayload, disabled: aiSummaryDisabled } =
    useAiSummary({
      dashboard: "Wellness Overview",
      groups,
      isComparing,
      dimension: comparison.dimension,
      filterParams,
      metricSpecs: [
        { label: "Recovery", keys: ["recovery", "recovery_score"] },
        { label: "Strain", keys: ["strain"] },
        { label: "Sleep performance", keys: ["sleep_performance"] },
        { label: "HRV", keys: ["average_hrv", "hrv"] },
      ],
    });

  const strainRecoveryCompareChart = useMemo(
    () =>
      buildComparisonTrendSeries(
        comparisonStrainRecovery.map((e) => ({
          label: e.group.label,
          color: e.group.color,
          data: transformTrendsData(e.data, ["recovery_score"]),
        })),
        "recovery_score",
        "label"
      ),
    [comparisonStrainRecovery]
  );
  const workoutCompareChart = useMemo(
    () =>
      buildComparisonTrendSeries(
        comparisonWorkout.map((e) => ({
          label: e.group.label,
          color: e.group.color,
          data: transformTrendsData(e.data, ["workout_time"]),
        })),
        "workout_time",
        "label"
      ),
    [comparisonWorkout]
  );
  const sleepCompareChart = useMemo(
    () =>
      buildComparisonTrendSeries(
        comparisonSleep.map((e) => ({
          label: e.group.label,
          color: e.group.color,
          data: transformTrendsData(e.data, ["hours_of_sleep"]),
        })),
        "hours_of_sleep",
        "label"
      ),
    [comparisonSleep]
  );

  const organizationHealthData = useMemo(() => {
    if (!wellbeingDistributionData?.data?.distribution) return [];
    return wellbeingDistributionData.data.distribution.map((item) => ({
      metric: item.label,
      currentScore: roundToDecimal(item.value),
      optimalTarget: 100,
    }));
  }, [wellbeingDistributionData]);

  // Radar with one series per group: merge each group's distribution onto a
  // shared list of metric labels, keyed group_<i>.
  const comparisonRadar = useMemo(() => {
    const rowsByMetric = new Map<string, Record<string, string | number>>();
    const seriesKeys: string[] = [];
    const seriesLabels: string[] = [];
    const colors: string[] = [];

    comparisonWellbeing.forEach((entry, index) => {
      const key = `group_${index}`;
      seriesKeys.push(key);
      seriesLabels.push(entry.group.label);
      colors.push(entry.group.color);
      (entry.data?.data?.distribution ?? []).forEach((item) => {
        const row = rowsByMetric.get(item.label) ?? { metric: item.label };
        row[key] = roundToDecimal(item.value);
        rowsByMetric.set(item.label, row);
      });
    });

    return {
      data: Array.from(rowsByMetric.values()),
      seriesKeys,
      seriesLabels,
      colors,
    };
  }, [comparisonWellbeing]);

  const comparisonRadarLoading = comparisonWellbeing.some((e) => e.isLoading);
  const strainRecoveryCompareLoading = comparisonStrainRecovery.some((e) => e.isLoading);
  const workoutCompareLoading = comparisonWorkout.some((e) => e.isLoading);
  const sleepCompareLoading = comparisonSleep.some((e) => e.isLoading);

  const departmentPerformanceData = useMemo(() => {
    const dataMap: Record<string, Array<{ label: string; value: number }>> = {};

    if (strainActivityByDepartmentData?.data?.distribution) {
      dataMap.strain_activity_by_department =
        strainActivityByDepartmentData.data.distribution.map((item) => ({
          label: item.label,
          value: roundToDecimal(item.value),
        }));
    }

    if (sleepQualityByDepartmentData?.data?.distribution) {
      dataMap.sleep_quality_by_department =
        sleepQualityByDepartmentData.data.distribution.map((item) => ({
          label: item.label,
          value: roundToDecimal(item.value),
        }));
    }

    if (workoutActivityByDepartmentData?.data?.distribution) {
      dataMap.workout_activity_by_department =
        workoutActivityByDepartmentData.data.distribution.map((item) => ({
          label: item.label,
          value: roundToDecimal(item.value),
        }));
    }

    return dataMap;
  }, [
    strainActivityByDepartmentData,
    sleepQualityByDepartmentData,
    workoutActivityByDepartmentData,
  ]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <ComparisonToggle comparison={comparison} />
            <DurationSelector
              value={dateRange}
              onChange={handleDateRangeChange}
              buttonVariant="outlined"
            />
            <Button
              variant="text"
              size="medium"
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? "primary" : "inherit"}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : "FILTER"}
            </Button>
          </Box>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filters}
        />

        {statsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load wellness statistics. Please try again.
          </Alert>
        )}

        {/* HRV gauge, wellness gauges and risk breakdown — one column per
            comparison group, or a single full-width row in average mode. */}
        {statsLoading ? (
          <DashboardCardSkeleton title subtitle height={200} />
        ) : (
          <ComparisonColumns groups={groups} overflowCount={overflowCount}>
            {(group) => (
              <WellnessOverviewStatsColumn
                filterParams={group.filterParams}
                isCentralAdmin={isCentralAdmin}
                isAnonymised={isAnonymised}
                stacked={isComparing}
                showBreakdowns
                expandedMetric={expandedBreakdownMetric}
                onToggleMetric={(metricId) =>
                  setExpandedBreakdownMetric((prev) =>
                    prev === metricId ? null : metricId
                  )
                }
              />
            )}
          </ComparisonColumns>
        )}

        <DashboardCard
          title="AI Summary"
          tooltip="An AI-generated narrative of the metrics currently shown."
          expandable={false}
        >
          <AiSummaryPanel
            title={
              isComparing
                ? "Compare these groups with AI"
                : "Summarise this view with AI"
            }
            buildPayload={buildAiSummaryPayload}
            disabled={aiSummaryDisabled}
          />
        </DashboardCard>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            {recoveryStrainTrendsLoading ? (
              <DashboardCardSkeleton title subtitle height={350} />
            ) : recoveryStrainTrendsError ? (
              <DashboardCard
                title="Strain & Recovery "
                subtitle="Daily trend of recovery versus strain"
                details={WELLNESS_OVERVIEW_DETAILS.strainAndRecovery}
              >
                <Alert severity="error">
                  Failed to load recovery and strain trends.
                </Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Strain & Recovery "
                subtitle="Daily trend of recovery versus strain"
                tooltip="Daily trend of recovery versus strain"
                details={WELLNESS_OVERVIEW_DETAILS.strainAndRecovery}
                titleComponent={
                  statsData?.data?.recovery_score ? (
                    <TextStat
                      title="Organisation Average"
                      value={`${roundToDecimal(
                        statsData.data.recovery_score.value
                      )}${statsData.data.recovery_score.unit || ""}`}
                      change={statsData.data.recovery_score.change}
                    />
                  ) : undefined
                }
              >
                <CohortGuard
                  userCount={activeUserCount}
                  bypass={isCentralAdmin || !isAnonymised}
                  loading={statsLoading}
                >
                  {isComparing ? (
                    strainRecoveryCompareLoading ? (
                      <Alert severity="info">Loading comparison…</Alert>
                    ) : strainRecoveryCompareChart.data.length > 0 ? (
                      <LineChart
                        data={strainRecoveryCompareChart.data}
                        series={strainRecoveryCompareChart.series}
                        height={300}
                        yAxisMin={0}
                        yAxisMax={100}
                      />
                    ) : (
                      <Alert severity="info">No trend data available.</Alert>
                    )
                  ) : strainRecoveryTrends.length > 0 ? (
                    <>
                      <LineChart
                        data={strainRecoveryTrends}
                        series={[
                          {
                            dataKey: "recovery_score",
                            label: "Recovery",
                            color: theme.palette.success.main,
                            area: true,
                          },
                          {
                            dataKey: "strain",
                            label: "Strain",
                            color: theme.palette.warning.main,
                            area: true,
                          },
                        ]}
                        height={300}
                        yAxisMin={0}
                        yAxisMax={100}
                        showMinMaxLines
                        referenceMin={overallMinMax.recoveryMin}
                        referenceMax={overallMinMax.recoveryMax}
                      />
                      {statsData?.data?.recovery_score?.rag_insight && (
                        <Alert icon={<InfoOutlinedIcon />} severity="info">
                          {statsData.data.recovery_score.rag_insight}
                        </Alert>
                      )}
                    </>
                  ) : (
                    <Alert severity="info">No trend data available.</Alert>
                  )}
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            {wellbeingDistributionLoading ? (
              <DashboardCardSkeleton title subtitle height={300} />
            ) : wellbeingDistributionError ? (
              <DashboardCard
                title="Organisation Health Overview"
                subtitle="Benchmark of company health across key metrics"
                details={WELLNESS_OVERVIEW_DETAILS.organisationHealthOverview}
              >
                <Alert severity="error">
                  Failed to load organisation health data.
                </Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Organisation Health Overview"
                subtitle="Benchmark of company health across key metrics"
                details={WELLNESS_OVERVIEW_DETAILS.organisationHealthOverview}
              >
                <CohortGuard
                  userCount={activeUserCount}
                  bypass={isCentralAdmin || !isAnonymised}
                  loading={statsLoading}
                >
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    {isComparing ? (
                      comparisonRadarLoading ? (
                        <Alert severity="info">Loading comparison…</Alert>
                      ) : comparisonRadar.data.length > 0 ? (
                        <RadarChart
                          data={comparisonRadar.data}
                          seriesKeys={comparisonRadar.seriesKeys}
                          seriesLabels={comparisonRadar.seriesLabels}
                          metricKey="metric"
                          height={300}
                          max={100}
                          colors={comparisonRadar.colors}
                        />
                      ) : (
                        <Alert severity="info">
                          No organisation health data available.
                        </Alert>
                      )
                    ) : organizationHealthData.length > 0 ? (
                      <RadarChart
                        data={organizationHealthData}
                        seriesKeys={["currentScore", "optimalTarget"]}
                        seriesLabels={["Your Organisation", "Optimal Target"]}
                        metricKey="metric"
                        height={300}
                        max={100}
                        colors={[
                          theme.palette.warning.main,
                          theme.palette.success.main,
                        ]}
                      />
                    ) : (
                      <Alert severity="info">
                        No organisation health data available.
                      </Alert>
                    )}
                  </Box>
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            {workoutTimeTrendsLoading ? (
              <DashboardCardSkeleton title subtitle height={350} />
            ) : workoutTimeTrendsError ? (
              <DashboardCard
                title="Workout Time"
                subtitle="Organisation-wide workout time trends"
                metricKey="workout_time"
                details={WELLNESS_OVERVIEW_DETAILS.workoutTime}
              >
                <Alert severity="error">
                  Failed to load workout time trends.
                </Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Workout Time"
                subtitle="Organisation-wide workout time trends"
                metricKey="workout_time"
                details={WELLNESS_OVERVIEW_DETAILS.workoutTime}
                titleComponent={
                  statsData?.data?.workout_time ? (
                    <TextStat
                      title="Organisation Average"
                      value={`${roundToDecimal(
                        statsData.data.workout_time.value
                      )}${statsData.data.workout_time.unit || ""}`}
                      change={statsData.data.workout_time.change}
                    />
                  ) : undefined
                }
              >
                <CohortGuard
                  userCount={activeUserCount}
                  bypass={isCentralAdmin || !isAnonymised}
                  loading={statsLoading}
                >
                  {isComparing ? (
                    workoutCompareLoading ? (
                      <Alert severity="info">Loading comparison…</Alert>
                    ) : workoutCompareChart.data.length > 0 ? (
                      <LineChart
                        data={workoutCompareChart.data}
                        series={workoutCompareChart.series}
                        height={300}
                        yAxisMin={0}
                      />
                    ) : (
                      <Alert severity="info">
                        No workout time trend data available.
                      </Alert>
                    )
                  ) : workoutTimeTrends.length > 0 ? (
                    <LineChart
                      data={workoutTimeTrends}
                      series={[
                        {
                          dataKey: "workout_time",
                          label: "Workout Time",
                          color: theme.palette.info.main,
                          area: true,
                        },
                      ]}
                      height={300}
                      yAxisMin={0}
                      showMinMaxLines
                      referenceMin={overallMinMax.workoutTimeMin}
                      referenceMax={overallMinMax.workoutTimeMax}
                    />
                  ) : (
                    <Alert severity="info">
                      No workout time trend data available.
                    </Alert>
                  )}
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            {sleepTrendsLoading ? (
              <DashboardCardSkeleton title subtitle height={350} />
            ) : sleepTrendsError ? (
              <DashboardCard
                title="Average Sleep Duration"
                subtitle="Comparison between average hours of sleep"
                metricKey="hours_of_sleep"
                details={WELLNESS_OVERVIEW_DETAILS.averageSleepDuration}
              >
                <Alert severity="error">Failed to load sleep trends.</Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Average Sleep Duration"
                subtitle="Comparison between average hours of sleep"
                metricKey="hours_of_sleep"
                details={WELLNESS_OVERVIEW_DETAILS.averageSleepDuration}
                titleComponent={
                  statsData?.data?.sleep_performance ? (
                    <TextStat
                      title="Organisation Average"
                      value={`${roundToDecimal(
                        statsData.data.sleep_performance.value
                      )}${statsData.data.sleep_performance.unit || ""}`}
                      change={statsData.data.sleep_performance.change}
                    />
                  ) : undefined
                }
              >
                <CohortGuard
                  userCount={activeUserCount}
                  bypass={isCentralAdmin || !isAnonymised}
                  loading={statsLoading}
                >
                  {isComparing ? (
                    sleepCompareLoading ? (
                      <Alert severity="info">Loading comparison…</Alert>
                    ) : sleepCompareChart.data.length > 0 ? (
                      <LineChart
                        data={sleepCompareChart.data}
                        series={sleepCompareChart.series}
                        height={300}
                        yAxisMin={0}
                      />
                    ) : (
                      <Alert severity="info">No sleep trend data available.</Alert>
                    )
                  ) : sleepTrends.length > 0 ? (
                    <LineChart
                      data={sleepTrends}
                      series={[
                        {
                          dataKey: "hours_of_sleep",
                          label: "Hours of Sleep",
                          color: theme.palette.info.main,
                          area: true,
                        },
                        {
                          dataKey: "hours_of_sleep_needed",
                          label: "Hours of Sleep Needed",
                          color: theme.palette.success.main,
                          area: true,
                        },
                      ]}
                      height={300}
                      yAxisMin={0}
                      showMinMaxLines
                      referenceMin={overallMinMax.hoursOfSleepMin}
                      referenceMax={overallMinMax.hoursOfSleepMax}
                    />
                  ) : (
                    <Alert severity="info">No sleep trend data available.</Alert>
                  )}
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            {strainActivityByDepartmentLoading ||
            sleepQualityByDepartmentLoading ||
            workoutActivityByDepartmentLoading ? (
              <DashboardCardSkeleton title subtitle height={300} />
            ) : strainActivityByDepartmentError ||
              sleepQualityByDepartmentError ||
              workoutActivityByDepartmentError ? (
              <DashboardCard
                title="Department Performance"
                subtitle="Average wellness performance by department, segmented across strain, recovery, and activity"
                details={WELLNESS_OVERVIEW_DETAILS.departmentPerformance}
              >
                <Alert severity="error">
                  Failed to load department performance data.
                </Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Department Performance"
                subtitle="Average wellness performance by department, segmented across strain, recovery, and activity"
                details={WELLNESS_OVERVIEW_DETAILS.departmentPerformance}
                titleComponent={
                  <ToggleButtonGroup
                    value={departmentView}
                    exclusive
                    onChange={(_, newValue) => {
                      if (newValue !== null) setDepartmentView(newValue);
                    }}
                    size="small"
                  >
                    <ToggleButton value="strain_activity_by_department">
                      Strain & Activity
                    </ToggleButton>
                    <ToggleButton value="sleep_quality_by_department">
                      Sleep Quality
                    </ToggleButton>
                    <ToggleButton value="workout_activity_by_department">
                      Workout Activity
                    </ToggleButton>
                  </ToggleButtonGroup>
                }
              >
                <CohortGuard
                  userCount={activeUserCount}
                  bypass={isCentralAdmin || !isAnonymised}
                  loading={statsLoading}
                >
                  {departmentPerformanceData[departmentView] &&
                  departmentPerformanceData[departmentView].length > 0 ? (
                    <HorizontalBarChart
                      data={departmentPerformanceData[departmentView]}
                      height={250}
                    />
                  ) : (
                    <Alert severity="info">
                      No department performance data available.
                    </Alert>
                  )}
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
